import styled, { keyframes } from "styled-components";
import { WRAPPED_2025_SEO } from "../../../seo/pageSeoMeta";
import { useState, useEffect, useMemo, useRef } from "react";
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Clock,
  TrendingDown,
  TrendingUp,
  Sun,
  Snowflake,
  Moon,
  MapPin,
  Award,
  AlertTriangle
} from 'lucide-react';
import StationMap, { Station } from './StationMap';
import LinesTable from './LinesTable';
import StationsTable from './StationsTable';

// CSV data imports
import deLineData from '../../../data/Wrapped2025/2025-train-ranking-DE.csv';
import atLineData from '../../../data/Wrapped2025/2025-train-ranking-AT.csv';
import beLineData from '../../../data/Wrapped2025/2025-train-ranking-BE.csv';
import frLineData from '../../../data/Wrapped2025/2025-train-ranking-FR.csv';
import nlLineData from '../../../data/Wrapped2025/2025-train-ranking-NL.csv';
import chLineData from '../../../data/Wrapped2025/2025-train-ranking-CH.csv';
import itLineData from '../../../data/Wrapped2025/2025-train-ranking-IT.csv';

import deStationData from '../../../data/Wrapped2025/2025-stop-ranking-DE.csv';
import atStationData from '../../../data/Wrapped2025/2025-stop-ranking-AT.csv';
import beStationData from '../../../data/Wrapped2025/2025-stop-ranking-BE.csv';
import frStationData from '../../../data/Wrapped2025/2025-stop-ranking-FR.csv';
import nlStationData from '../../../data/Wrapped2025/2025-stop-ranking-NL.csv';
import chStationData from '../../../data/Wrapped2025/2025-stop-ranking-CH.csv';
import itStationData from '../../../data/Wrapped2025/2025-stop-ranking-IT.csv';
import stationDelaysWithCoordinates from '../../../data/Wrapped2025/station_delays_with_coordinates-2025-12-09_52121.csv';

// Color palette matching the design
const colors = {
  slate950: '#0F172A',
  slate900: '#1E293B',
  slate800: '#1E293B',
  slate700: '#334155',
  slate600: '#475569',
  slate300: '#CBD5E1',
  slate400: '#94A3B8',
  emerald500: '#10B981',
  emerald400: '#34D399',
  blue500: '#3B82F6',
  blue400: '#60A5FA',
  blue600: '#2563EB',
  cyan400: '#22D3EE',
  orange500: '#F97316',
  orange400: '#FB923C',
  red500: '#EF4444',
  red400: '#F87171',
  yellow400: '#FACC15',
  yellow500: '#EAB308',
};

// Animations
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const scroll = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-33.333%);
  }
`;

const scrollReverse = keyframes`
  0% {
    transform: translateX(-33.333%);
  }
  100% {
    transform: translateX(0);
  }
`;

// Main container with custom font
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom, ${colors.slate950}, ${colors.slate900}, ${colors.slate950});
  color: white;
  position: relative;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  
  * {
    font-family: inherit;
  }
`;

// Scroll progress bar
const ScrollProgress = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 4px;
  width: 0%;
  background: linear-gradient(to right, ${colors.emerald500}, ${colors.blue500}, ${colors.orange500});
  will-change: width;
  z-index: 1001;
`;

// Section base
const Section = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5rem 1rem;
  position: relative;
  overflow: hidden;
  
  @media (min-width: 768px) {
    padding: 5rem 2rem;
  }
`;

// Closing section with extended gradient
const ClosingSection = styled(Section)`
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -100px;
    left: -100px;
    right: -100px;
    bottom: 0;
    background: linear-gradient(to top, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 30%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }
`;

// Hero Section
const HeroSection = styled(Section)`
  min-height: 70vh;
  
  @media (min-width: 768px) {
    min-height: 80vh;
  }
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom right, rgba(59, 130, 246, 0.1), transparent, rgba(249, 115, 22, 0.1));
  }
`;

const StarField = styled.div`
  position: absolute;
  inset: 0;
  
  div {
    position: absolute;
    width: 4px;
    height: 4px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    animation: ${pulse} 2s ease-in-out infinite;
  }
`;

const HeroContent = styled.div`
  text-align: center;
  z-index: 10;
  max-width: 56rem;
  position: relative;
`;

const TrainCarousel = styled.div`
  width: 100vw;
  overflow: hidden;
  margin-bottom: 2rem;
  position: absolute;
  left: 0;
  margin-left: calc(-50vw + 50%);
  height: 80px;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  
  @media (min-width: 768px) {
    height: 100px;
    margin-bottom: 3rem;
  }
`;

const TrainTrack = styled.div<{ reverse?: boolean; speed?: number }>`
  display: flex;
  gap: 2rem;
  width: fit-content;
  animation: ${({ reverse }) => reverse ? scrollReverse : scroll} ${({ speed }) => speed || 30}s linear infinite;
  will-change: transform;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  
  @media (min-width: 768px) {
    gap: 3rem;
  }
`;

const TrainIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  img {
    width: 4rem;
    height: auto;
    opacity: 0.3;
    filter: brightness(0.4);
    transition: opacity 0.3s ease;
    
    &:hover {
      opacity: 0.5;
    }
    
    @media (min-width: 768px) {
      width: 5rem;
    }
  }
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  background: linear-gradient(to right, ${colors.blue400}, ${colors.emerald400}, ${colors.orange400});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (min-width: 768px) {
    font-size: 4.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  color: ${colors.slate300};
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ScrollHint = styled.div`
  font-size: 0.875rem;
  color: ${colors.slate400};
`;

// Content Card
const ContentCard = styled.div<{ visible?: boolean }>`
  max-width: 56rem;
  width: 100%;
  margin: 0 auto;
  text-align: center;
  transform: ${({ visible }) => visible ? 'translateY(0)' : 'translateY(30px)'};
  opacity: ${({ visible }) => visible ? 1 : 0};
  transition: all 1s ease;
`;

// Section Wrapper for consistent centering
const SectionWrapper = styled.div<{ maxWidth?: string }>`
  max-width: ${({ maxWidth }) => maxWidth || '56rem'};
  width: 100%;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 1.875rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: ${colors.slate300};
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.25rem;
  color: ${colors.slate300};
  margin-bottom: 3rem;
`;

const BigNumberCard = styled.div`
  background: linear-gradient(to bottom right, rgba(249, 115, 22, 0.2), rgba(239, 68, 68, 0.2));
  border-radius: 1.5rem;
  padding: 2rem;
  border: 1px solid rgba(249, 115, 22, 0.3);
  backdrop-filter: blur(4px);
  
  @media (min-width: 768px) {
    padding: 3rem;
  }
`;

const BigNumber = styled.div`
  font-size: 4.5rem;
  font-weight: 900;
  margin-bottom: 1rem;
  background: linear-gradient(to right, ${colors.orange400}, ${colors.red400});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (min-width: 768px) {
    font-size: 6rem;
  }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 3rem;
  
  @media (min-width: 768px) {
    gap: 2rem;
  }
`;

const StatCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  border-radius: 0.75rem;
  padding: 1rem;
  border: 1px solid ${colors.slate700};
  
  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

const StatValue = styled.div<{ color: string }>`
  font-size: 1.875rem;
  font-weight: 700;
  color: ${({ color }) => color};
  
  @media (min-width: 768px) {
    font-size: 2.25rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: ${colors.slate400};
  margin-top: 0.5rem;
  
  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

// Peak Chaos Section
const PeakChaosCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  border-radius: 1.5rem;
  padding: 1.5rem;
  border: 1px solid ${colors.slate700};
  backdrop-filter: blur(4px);
  
  @media (min-width: 768px) {
    padding: 2.5rem;
  }
`;

const TimeDisplay = styled.div`
  margin-bottom: 2rem;
  
  div:first-child {
    font-size: 3.75rem;
    font-weight: 900;
    color: ${colors.orange400};
    margin-bottom: 0.5rem;
    
    @media (min-width: 768px) {
      font-size: 5rem;
    }
  }
  
  p {
    font-size: 1.125rem;
    color: ${colors.slate300};
    
    @media (min-width: 768px) {
      font-size: 1.25rem;
    }
  }
`;

const BarChart = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.25rem;
  height: 16rem;
  min-height: 16rem;
  margin-bottom: 2rem;
  position: relative;
`;

// Helper function to calculate gradient colors based on bar height (0-100%)
const getBarGradient = (height: number): string => {
  // Normalize height to 0-1
  const normalized = Math.max(0, Math.min(100, height)) / 100;
  
  if (normalized <= 0.3) {
    // Low delays: Blue gradient
    // Interpolate between light blue and medium blue
    const intensity = normalized / 0.3;
    const r1 = Math.round(6 + (59 - 6) * intensity);
    const g1 = Math.round(182 + (130 - 182) * intensity);
    const b1 = Math.round(212 + (212 - 212) * intensity);
    const r2 = Math.round(3 + (59 - 3) * intensity);
    const g2 = Math.round(105 + (130 - 105) * intensity);
    const b2 = Math.round(140 + (212 - 140) * intensity);
    return `linear-gradient(to top, rgb(${r1}, ${g1}, ${b1}), rgb(${r2}, ${g2}, ${b2}))`;
  } else if (normalized <= 0.6) {
    // Medium delays: Yellow to Orange gradient
    // Interpolate between blue-yellow transition and orange
    const intensity = (normalized - 0.3) / 0.3;
    const r1 = Math.round(234 + (249 - 234) * intensity);
    const g1 = Math.round(179 + (115 - 179) * intensity);
    const b1 = Math.round(8 + (22 - 8) * intensity);
    const r2 = Math.round(249 + (239 - 249) * intensity);
    const g2 = Math.round(115 + (68 - 115) * intensity);
    const b2 = Math.round(22 + (68 - 22) * intensity);
    return `linear-gradient(to top, rgb(${r1}, ${g1}, ${b1}), rgb(${r2}, ${g2}, ${b2}))`;
  } else {
    // High delays: Orange to Dark Red gradient
    // Interpolate between orange and dark red (closer to 100% = darker red)
    const intensity = (normalized - 0.6) / 0.4;
    const r1 = Math.round(249 + (185 - 249) * intensity);
    const g1 = Math.round(115 + (28 - 115) * intensity);
    const b1 = Math.round(22 + (28 - 22) * intensity);
    const r2 = Math.round(239 + (127 - 239) * intensity);
    const g2 = Math.round(68 + (29 - 68) * intensity);
    const b2 = Math.round(68 + (29 - 68) * intensity);
    return `linear-gradient(to top, rgb(${r1}, ${g1}, ${b1}), rgb(${r2}, ${g2}, ${b2}))`;
  }
};

const Bar = styled.div<{ height: number; visible: boolean; delay: number }>`
  width: 100%;
  border-radius: 0.25rem 0.25rem 0 0;
  transition: height 1s ease, opacity 0.5s ease, background 0.5s ease;
  transition-delay: ${({ delay }) => delay}ms;
  height: ${({ visible, height }) => {
    if (!visible) return '0';
    // Convert percentage to rem (parent is 16rem)
    // height is 0-100, so multiply by 16rem / 100
    const heightInRem = (height / 100) * 16;
    return `${heightInRem}rem`;
  }};
  opacity: ${({ visible }) => visible ? 1 : 0};
  background: ${({ height }) => getBarGradient(height)};
  min-height: ${({ visible, height }) => {
    if (!visible) return '0';
    // Ensure even small bars are visible (at least 4px)
    return height < 1 ? '4px' : '0';
  }};
  align-self: flex-end;
  max-height: none;
`;

const HourLabel = styled.div`
  font-size: 0.75rem;
  color: ${colors.slate400};
  margin-top: 0.5rem;
  text-align: center;
`;

const BarWrapper = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
  
  &:hover .tooltip {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(-8px);
  }
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(-8px);
  background: rgba(30, 41, 59, 0.95);
  backdrop-filter: blur(8px);
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  border: 1px solid ${colors.slate700};
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.4);
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  pointer-events: none;
  z-index: 10;
  margin-bottom: 0.5rem;
  min-width: 140px;
  text-align: center;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: rgba(30, 41, 59, 0.95);
  }
`;

const TooltipTitle = styled.div`
  font-size: 0.75rem;
  color: ${colors.slate400};
  margin-bottom: 0.25rem;
  font-weight: 500;
`;

const TooltipValue = styled.div`
  font-size: 1rem;
  color: ${colors.slate300};
  font-weight: 600;
`;

const InfoBox = styled.div`
  background: rgba(15, 23, 42, 0.5);
  border-radius: 0.75rem;
  padding: 1.5rem;
  border: 1px solid ${colors.slate700};
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  text-align: left;
  
  svg {
    width: 2rem;
    height: 2rem;
    color: ${colors.blue400};
    flex-shrink: 0;
  }
`;

const InfoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const InfoTitle = styled.div`
  font-weight: 700;
  font-size: 1.125rem;
  margin-bottom: 0.5rem;
  color: ${colors.blue400};
`;

const InfoText = styled.p`
  color: ${colors.slate300};
  line-height: 1.6;
  margin: 0;
  margin-left: calc(2rem + 0.75rem); /* Icon width + gap to align with title */
  text-align: left !important;
`;

// Day Cards
const DayCard = styled.div<{ variant: 'worst' | 'best' }>`
  background: ${({ variant }) => 
    variant === 'worst' 
      ? 'linear-gradient(to bottom right, rgba(249, 115, 22, 0.2), rgba(239, 68, 68, 0.2))'
      : 'linear-gradient(to bottom right, rgba(16, 185, 129, 0.2), rgba(59, 130, 246, 0.2))'};
  border-radius: 1.5rem;
  padding: 2rem;
  border: 1px solid ${({ variant }) => 
    variant === 'worst' 
      ? 'rgba(249, 115, 22, 0.3)'
      : 'rgba(16, 185, 129, 0.3)'};
  backdrop-filter: blur(4px);
  
  @media (min-width: 768px) {
    padding: 3rem;
  }
`;

const DayDate = styled.div<{ variant?: 'worst' | 'best' }>`
  font-size: 3rem;
  font-weight: 900;
  margin-bottom: 1rem;
  color: ${({ variant }) => 
    variant === 'worst' ? colors.orange400 : colors.emerald400};
  
  @media (min-width: 768px) {
    font-size: 4rem;
  }
`;

const DayTitle = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    font-size: 1.875rem;
  }
`;

const DayStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const DayStatCard = styled.div`
  background: rgba(15, 23, 42, 0.5);
  border-radius: 0.75rem;
  padding: 1rem;
  text-align: center;
`;

// Country Rankings
const CountryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 80rem;
  width: 100%;
`;

const CountryCard = styled.div<{ visible: boolean; delay: number }>`
  background: rgba(30, 41, 59, 0.5);
  border-radius: 1rem;
  padding: 1.5rem;
  border: 1px solid ${colors.slate700};
  backdrop-filter: blur(4px);
  transform: ${({ visible }) => visible ? 'translateX(0)' : 'translateX(-50px)'};
  opacity: ${({ visible }) => visible ? 1 : 0};
  transition: all 0.7s ease;
  transition-delay: ${({ delay }) => delay}ms;
  
  &:hover {
    transform: scale(1.02);
  }
`;

const CountryHeader = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const RankNumber = styled.div<{ rank: number }>`
  font-size: 1.875rem;
  font-weight: 900;
  color: ${({ rank }) => 
    rank === 1 ? colors.yellow400 :
    rank === 2 ? colors.slate300 :
    rank === 3 ? colors.orange500 :
    colors.slate400};
  min-width: 3rem;
  
  @media (max-width: 640px) {
    font-size: 1.5rem;
  }
`;

const CountryInfo = styled.div`
  min-width: 0;
  width: 100%;
  
  div:first-child {
    font-weight: 700;
    font-size: 1.25rem;
    margin-bottom: 0.25rem;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    @media (max-width: 640px) {
      font-size: 1.125rem;
    }
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  min-width: 200px;
  background: ${colors.slate700};
  border-radius: 9999px;
  height: 0.75rem;
  overflow: hidden;
  
  @media (max-width: 640px) {
    min-width: 100%;
  }
`;

const ProgressFill = styled.div<{ width: number; visible: boolean; color: string }>`
  height: 100%;
  border-radius: 9999px;
  background: ${({ color }) => color};
  width: ${({ visible, width }) => visible ? `${width}%` : '0%'};
  transition: width 1s ease;
`;

const CountryStats = styled.div`
  display: flex;
  gap: 1.5rem;
  text-align: center;
  min-width: 180px;
  justify-content: flex-end;
  
  @media (min-width: 768px) {
    gap: 2.5rem;
    min-width: 220px;
  }
  
  @media (max-width: 640px) {
    justify-content: flex-start;
    min-width: auto;
  }
`;

const CountryStat = styled.div`
  min-width: 80px;
  width: 80px;
  
  @media (min-width: 768px) {
    min-width: 100px;
    width: 100px;
  }
  
  div:first-child {
    font-size: 1.5rem;
    font-weight: 700;
    
    @media (min-width: 768px) {
      font-size: 1.875rem;
    }
  }
  
  div:last-child {
    font-size: 0.75rem;
    color: ${colors.slate400};
  }
`;

// Train List
const TrainList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const TrainCard = styled.div<{ visible: boolean; delay: number }>`
  background: rgba(15, 23, 42, 0.5);
  border-radius: 0.75rem;
  padding: 1.5rem;
  border: 1px solid ${colors.slate700};
  opacity: ${({ visible }) => visible ? 1 : 0};
  transition: opacity 0.7s ease;
  transition-delay: ${({ delay }) => delay}ms;
`;

const TrainHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const TrainRoute = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`;

const TrainLineInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const TrainLineName = styled.div`
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', 'Monaco', 'Courier New', monospace;
  font-weight: 700;
  font-size: 1.125rem;
  color: ${colors.blue400};
`;

const TrainRouteText = styled.div`
  color: white;
  font-size: 1.125rem;
`;

const TrainDelay = styled.div`
  text-align: right;
  
  div:first-child {
    font-size: 1.875rem;
    font-weight: 900;
    color: ${colors.orange400};
    line-height: 1.2;
  }
  
  div:last-child {
    font-size: 0.875rem;
    color: white;
    margin-top: 0.25rem;
  }
`;

// Station Cards
const StationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StationCard = styled.div<{ variant: 'best' | 'worst'; visible: boolean }>`
  background: ${({ variant }) => 
    variant === 'best'
      ? 'linear-gradient(to bottom right, rgba(16, 185, 129, 0.2), rgba(34, 211, 153, 0.2))'
      : 'linear-gradient(to bottom right, rgba(239, 68, 68, 0.2), rgba(249, 115, 22, 0.2))'};
  border-radius: 1.5rem;
  padding: 1.5rem;
  border: 1px solid ${({ variant }) => 
    variant === 'best'
      ? 'rgba(16, 185, 129, 0.3)'
      : 'rgba(239, 68, 68, 0.3)'};
  backdrop-filter: blur(4px);
  transform: ${({ visible, variant }) => 
    visible ? 'translateX(0)' : variant === 'best' ? 'translateX(-40px)' : 'translateX(40px)'};
  opacity: ${({ visible }) => visible ? 1 : 0};
  transition: all 0.7s ease;
  transition-delay: ${({ variant }) => variant === 'worst' ? '300ms' : '0ms'};
  display: flex;
  flex-direction: column;
`;

// Styled icon wrapper for animations
const AnimatedIconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  animation: ${spin} 10s linear infinite;
`;

// Icon component using lucide-react
const Icon = ({ name, size = 24, className = '', color, animate }: { name: string; size?: number; className?: string; color?: string; animate?: boolean }) => {
  const icons: Record<string, React.ComponentType<{ size?: number; color?: string; className?: string }>> = {
    clock: Clock,
    trendingDown: TrendingDown,
    trendingUp: TrendingUp,
    sun: Sun,
    snowflake: Snowflake,
    moon: Moon,
    mapPin: MapPin,
    award: Award,
    alertTriangle: AlertTriangle,
  };
  
  const IconComponent = icons[name];
  
  if (!IconComponent) {
    return <span className={className} style={{ fontSize: `${size}px` }}>•</span>;
  }
  
  const iconElement = <IconComponent size={size} color={color} className={className} />;
  
  if (animate) {
    return <AnimatedIconWrapper>{iconElement}</AnimatedIconWrapper>;
  }
  
  return iconElement;
};

const CTACard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  border-radius: 1rem;
  padding: 0rem 2.5rem 2.5rem 2.5rem;
  border: 1px solid ${colors.slate700};
  backdrop-filter: blur(4px);
  margin-bottom: 3rem;
  text-align: center;
  
  @media (min-width: 768px) {
    padding: 2.5rem 3rem 3rem 3rem;
  }
`;

const CTATitle = styled.h3`
  font-size: 1.875rem;
  font-weight: 700;
  color: white;
  margin-top: 0;
  margin-bottom: 0.75rem;
  
  @media (min-width: 768px) {
    font-size: 2.25rem;
  }
`;

const CTASubtitle = styled.p`
  font-size: 1.125rem;
  color: ${colors.slate300};
  margin-bottom: 2rem;
  line-height: 1.6;
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const CTAButton = styled.button`
  background: linear-gradient(to right, #9333EA, #A855F7);
  color: white;
  border: none;
  border-radius: 0.75rem;
  padding: 1rem 2rem;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
  
  &:hover {
    background: linear-gradient(to right, #7E22CE, #9333EA);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(147, 51, 234, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (min-width: 768px) {
    padding: 1.25rem 2.5rem;
    font-size: 1.25rem;
  }
`;

const SecondaryCTACard = styled(CTACard)`
  margin-top: 1.5rem;
  padding: 1.5rem 2rem;
  
  @media (min-width: 768px) {
    padding: 2rem 2.5rem;
  }
`;

const SecondaryCTATitle = styled.h4`
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
  margin: 0 0 0.5rem 0;
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SecondaryCTASubtitle = styled.p`
  font-size: 0.875rem;
  color: ${colors.slate300};
  margin-bottom: 1.5rem;
  line-height: 1.5;
  
  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const SecondaryCTAButton = styled.button`
  background: rgba(51, 65, 85, 0.6);
  color: white;
  border: 1px solid ${colors.slate700};
  border-radius: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
  
  &:hover {
    background: rgba(51, 65, 85, 0.8);
    border-color: ${colors.slate600};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (min-width: 768px) {
    padding: 0.875rem 2rem;
    font-size: 1rem;
  }
`;

const CountryDropdown = styled.select`
  padding: 1rem 3rem 1rem 1.5rem;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid ${colors.slate700};
  border-radius: 0.5rem;
  color: white;
  font-size: 1.125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 200px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='white' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  
  &:focus {
    outline: none;
    border-color: ${colors.blue400};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &:hover {
    border-color: ${colors.slate600};
  }
  
  option {
    background: ${colors.slate900};
    color: white;
    padding: 0.5rem;
  }
  
  @media (min-width: 768px) {
    padding: 1.25rem 3.5rem 1.25rem 2rem;
    font-size: 1.25rem;
    min-width: 250px;
    background-position: right 1.5rem center;
  }
`;

const DiveDeepSection = styled(Section)`
  gap: 2rem;
  
  @media (min-width: 768px) {
    gap: 3rem;
  }
`;

type CountryCode = 'DE' | 'AT' | 'BE' | 'FR' | 'NL' | 'CH' | 'IT';

interface TrainLine {
  originalRoute: string;
  originStation: string;
  destinationStation: string;
  avg_max_delay_minutes: number;
  cancellation_rate: number;
  punctuality_pct: number;
}

interface StationData {
  stopName: string;
  avg_delay_minutes: number;
  punctuality_pct: number;
}

const lineDataMap: Record<CountryCode, string> = {
  DE: deLineData,
  AT: atLineData,
  BE: beLineData,
  FR: frLineData,
  NL: nlLineData,
  CH: chLineData,
  IT: itLineData,
};

const stationDataMap: Record<CountryCode, string> = {
  DE: deStationData,
  AT: atStationData,
  BE: beStationData,
  FR: frStationData,
  NL: nlStationData,
  CH: chStationData,
  IT: itStationData,
};

const countryFlags: Record<CountryCode, string> = {
  DE: '🇩🇪',
  AT: '🇦🇹',
  BE: '🇧🇪',
  FR: '🇫🇷',
  NL: '🇳🇱',
  CH: '🇨🇭',
  IT: '🇮🇹',
};

const Wrapped2025Page = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scrollProgressRef = useRef<HTMLDivElement>(null);
  const [visibleSections, setVisibleSections] = useState<Record<string, boolean>>({});
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>('DE');
  const [lineData, setLineData] = useState<TrainLine[]>([]);
  const [stationData, setStationData] = useState<StationData[]>([]);
  const [mapStations, setMapStations] = useState<Station[]>([]);
  
  // Memoize country names with translations
  const countryNames: Record<CountryCode, string> = useMemo(() => ({
    DE: t('wrapped2025.countryNames.germany'),
    AT: t('wrapped2025.countryNames.austria'),
    BE: t('wrapped2025.countryNames.belgium'),
    FR: t('wrapped2025.countryNames.france'),
    NL: t('wrapped2025.countryNames.netherlands'),
    CH: t('wrapped2025.countryNames.switzerland'),
    IT: t('wrapped2025.countryNames.italy'),
  }), [t]);
  
  // Memoize map props to prevent unnecessary re-renders
  const mapCenter = useMemo(() => [10.5, 50.5] as [number, number], []);
  const mapZoom = useMemo(() => 5, []);
  const mapboxToken = useMemo(() => process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || "pk.eyJ1IjoiYXJuZW55c2NodXVjaHV1IiwiYSI6ImNtaXluODR3ZTBldnkzZ3NkODE2cWh6MzkifQ.ekhgFoHug_qEgoETJmuPXA", []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
      const pct = totalScroll > 0 ? (window.scrollY / totalScroll) * 100 : 0;
      if (scrollProgressRef.current) {
        scrollProgressRef.current.style.width = `${pct}%`;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection Observer for animations
  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    const createObserver = (id: string, threshold = 0.3, rootMargin?: string) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => ({ ...prev, [id]: true }));
          }
        },
        { threshold, rootMargin: rootMargin || '0px' }
      );

      const element = document.getElementById(id);
      if (element) {
        // Check if element is already in view
        const rect = element.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
        if (isVisible) {
          setVisibleSections(prev => ({ ...prev, [id]: true }));
        }
        
        observer.observe(element);
        observers.push(observer);
      }
    };

    ['bigNumber', 'peakChaos', 'hottestDay', 'christmas', 'countries', 'nightTrains', 'stations', 'diveDeep'].forEach(id => {
      if (id === 'countries') {
        createObserver(id, 0.2);
      } else if (id === 'diveDeep') {
        createObserver(id, 0.1, '200px 0px');
      } else {
        createObserver(id, 0.3);
      }
    });

    return () => {
      observers.forEach(obs => obs.disconnect());
    };
  }, []);

  // Load CSV data
  useEffect(() => {
    const parseCSVData = <T,>(csvString: string): T[] => {
      if (!csvString || csvString.trim() === '') {
        console.warn('Empty CSV string');
        return [];
      }
      
      const lines = csvString.split('\n').filter(line => line.trim() !== '');
      if (lines.length < 2) {
        console.warn('CSV has less than 2 lines');
        return [];
      }
      
      // Parse header - handle quoted headers
      const headerLine = lines[0];
      const headers: string[] = [];
      let currentHeader = '';
      let inQuotes = false;
      
      for (let i = 0; i < headerLine.length; i++) {
        const char = headerLine[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          headers.push(currentHeader.trim().replace(/^"|"$/g, ''));
          currentHeader = '';
        } else {
          currentHeader += char;
        }
      }
      if (currentHeader) {
        headers.push(currentHeader.trim().replace(/^"|"$/g, ''));
      }
      
      console.log('CSV Headers:', headers);
      
      return lines.slice(1)
        .filter(line => line.trim() !== '')
        .map((line, lineIndex) => {
          // Parse CSV line handling quoted values
          const values: string[] = [];
          let current = '';
          let inQuotes = false;
          
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
              values.push(current.trim().replace(/^"|"$/g, ''));
              current = '';
            } else {
              current += char;
            }
          }
          if (current) {
            values.push(current.trim().replace(/^"|"$/g, ''));
          }
          
          const obj: Record<string, string | number> = {};
          headers.forEach((header, index) => {
            const rawValue = values[index] || '';
            // Try to parse as number if it's a valid number
            const numValue = parseFloat(rawValue);
            if (rawValue !== '' && !isNaN(numValue) && isFinite(numValue)) {
              obj[header] = numValue;
            } else {
              obj[header] = rawValue;
            }
          });
          return obj as unknown as T;
        });
    };

    const loadData = async () => {
      try {
        // Load line data - CSV files are imported as URLs, need to fetch them
        const lineResponse = await fetch(lineDataMap[selectedCountry]);
        if (!lineResponse.ok) {
          console.error('Failed to load line data for country:', selectedCountry);
          return;
        }
        const lineText = await lineResponse.text();
        const parsedLineData = parseCSVData<TrainLine>(lineText);
        console.log('Loaded line data:', parsedLineData.length, 'rows for', selectedCountry);
        if (parsedLineData.length > 0) {
          console.log('First line:', parsedLineData[0]);
        }
        setLineData(parsedLineData);

        // Load station data - CSV files are imported as URLs, need to fetch them
        const stationResponse = await fetch(stationDataMap[selectedCountry]);
        if (!stationResponse.ok) {
          console.error('Failed to load station data for country:', selectedCountry);
          return;
        }
        const stationText = await stationResponse.text();
        const parsedStationData = parseCSVData<StationData>(stationText);
        console.log('Loaded station data:', parsedStationData.length, 'rows for', selectedCountry);
        if (parsedStationData.length > 0) {
          console.log('First station:', parsedStationData[0]);
        }
        setStationData(parsedStationData);
      } catch (error) {
        console.error('Error loading CSV:', error);
      }
    };

    loadData();
  }, [selectedCountry]);

  // Load station map data with coordinates
  useEffect(() => {
    const loadStationMapData = async () => {
      try {
        const parseCSVData = <T,>(csvString: string): T[] => {
          if (!csvString || csvString.trim() === '') {
            return [];
          }
          
          const lines = csvString.split('\n').filter(line => line.trim() !== '');
          if (lines.length < 2) {
            return [];
          }
          
          // Parse header - handle quoted headers
          const headerLine = lines[0];
          const headers: string[] = [];
          let currentHeader = '';
          let inQuotes = false;
          
          for (let i = 0; i < headerLine.length; i++) {
            const char = headerLine[i];
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
              headers.push(currentHeader.trim().replace(/^"|"$/g, ''));
              currentHeader = '';
            } else {
              currentHeader += char;
            }
          }
          headers.push(currentHeader.trim().replace(/^"|"$/g, ''));
          
          // Parse data rows
          const rows: T[] = [];
          for (let i = 1; i < lines.length; i++) {
            const line = lines[i];
            const values: string[] = [];
            let currentValue = '';
            let inQuotes = false;
            
            for (let j = 0; j < line.length; j++) {
              const char = line[j];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === ',' && !inQuotes) {
                values.push(currentValue.trim().replace(/^"|"$/g, ''));
                currentValue = '';
              } else {
                currentValue += char;
              }
            }
            values.push(currentValue.trim().replace(/^"|"$/g, ''));
            
            if (values.length === headers.length) {
              const row: any = {};
              headers.forEach((header, index) => {
                row[header] = values[index];
              });
              rows.push(row as T);
            }
          }
          
          return rows;
        };

        interface StationMapData {
          deutscheBahnStopId: string;
          stopName: string;
          avg_delay_minutes: string;
          punctuality_pct: string;
          latitude: string;
          longitude: string;
        }

        const response = await fetch(stationDelaysWithCoordinates);
        if (!response.ok) {
          console.error('Failed to load station map data');
          return;
        }
        const csvText = await response.text();
        const parsedData = parseCSVData<StationMapData>(csvText);
        
        // Country mapping based on station ID prefix
        const getCountryFromId = (id: string): string => {
          if (!id || id.length < 2) return 'Unknown';
          const prefix = id.substring(0, 2);
          switch (prefix) {
            case '80': return t('wrapped2025.countryNames.germany');
            case '81': return t('wrapped2025.countryNames.austria');
            case '82': return t('wrapped2025.countryNames.luxembourg');
            case '83': return t('wrapped2025.countryNames.italy');
            case '84': return t('wrapped2025.countryNames.netherlands');
            case '85': return t('wrapped2025.countryNames.switzerland');
            case '86': return t('wrapped2025.countryNames.denmark');
            case '87': return t('wrapped2025.countryNames.france');
            case '88': return t('wrapped2025.countryNames.belgium');
            default: return t('wrapped2025.countryNames.unknown');
          }
        };
        
        // Transform to Station[] format and filter out stations without coordinates
        const stations: Station[] = parsedData
          .filter(row => row.latitude && row.longitude && row.latitude.trim() !== '' && row.longitude.trim() !== '')
          .map(row => {
            const lat = parseFloat(row.latitude);
            const lng = parseFloat(row.longitude);
            const punctuality = parseFloat(row.punctuality_pct) || 0;
            const avgDelay = parseFloat(row.avg_delay_minutes) || 0;
            const country = getCountryFromId(row.deutscheBahnStopId);
            
            return {
              name: row.stopName,
              country,
              lat,
              lng,
              punctuality,
              avgDelay,
            };
          });
        
        setMapStations(stations);
      } catch (error) {
        console.error('Error loading station map data:', error);
      }
    };

    loadStationMapData();
  }, []);

  const countries = [
    { code: 'CH', name: 'Switzerland', punctuality: 97.79, delay: 1.4, color: `linear-gradient(to right, ${colors.emerald500}, ${colors.emerald400})`, rank: 1 },
    { code: 'NL', name: 'Netherlands', punctuality: 93.91, delay: 11.4, color: `linear-gradient(to right, ${colors.emerald500}, ${colors.emerald400})`, rank: 2 },
    { code: 'BE', name: 'Belgium', punctuality: 88.62, delay: 13.5, color: `linear-gradient(to right, ${colors.blue500}, ${colors.blue400})`, rank: 3 },
    { code: 'AT', name: 'Austria', punctuality: 82.17, delay: 10.4, color: `linear-gradient(to right, ${colors.blue500}, ${colors.blue400})`, rank: 4 },
    { code: 'FR', name: 'France', punctuality: 79.70, delay: 9.5, color: `linear-gradient(to right, ${colors.yellow500}, ${colors.yellow400})`, rank: 5 },
    { code: 'IT', name: 'Italy', punctuality: 62.00, delay: 22.7, color: `linear-gradient(to right, ${colors.orange500}, ${colors.orange400})`, rank: 6 },
    { code: 'DE', name: 'Germany', punctuality: 58.55, delay: 66.8, color: `linear-gradient(to right, ${colors.red500}, ${colors.red400})`, rank: 7 },
  ];

  // Hourly delay data (delay_sum in minutes)
  const hourlyDelayData = [
    { hour: 0, delaySum: 19676949 },
    { hour: 1, delaySum: 17886413 },
    { hour: 2, delaySum: 19776101 },
    { hour: 3, delaySum: 30261436 },
    { hour: 4, delaySum: 49170659 },
    { hour: 5, delaySum: 67868735 },
    { hour: 6, delaySum: 91395286 },
    { hour: 7, delaySum: 114619235 },
    { hour: 8, delaySum: 125359115 },
    { hour: 9, delaySum: 128626023 },
    { hour: 10, delaySum: 132242354 },
    { hour: 11, delaySum: 137253293 },
    { hour: 12, delaySum: 140491854 },
    { hour: 13, delaySum: 144779467 },
    { hour: 14, delaySum: 151165675 },
    { hour: 15, delaySum: 162042105 },
    { hour: 16, delaySum: 172751274 },
    { hour: 17, delaySum: 178304355 },
    { hour: 18, delaySum: 176817122 },
    { hour: 19, delaySum: 166169933 },
    { hour: 20, delaySum: 144477330 },
    { hour: 21, delaySum: 109202705 },
    { hour: 22, delaySum: 70369225 },
    { hour: 23, delaySum: 35214796 },
  ];

  // Find max delay for normalization
  const maxDelay = Math.max(...hourlyDelayData.map(d => d.delaySum));
  
  // Convert delay sums to percentages for bar heights (0-100%)
  const delayPercentages = hourlyDelayData.map(d => {
    return (d.delaySum / maxDelay) * 100;
  });

  const topDelayed = [
    { route: 'EN 345', from: 'Stockholm', to: 'Berlin', delay: 81.1 },
    { route: 'NJ 420', from: 'Innsbruck', to: 'Amsterdam', delay: 80.8 },
    { route: 'NJ 40490', from: 'Wien', to: 'Amsterdam', delay: 79.1 },
  ];

  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>{WRAPPED_2025_SEO.title}</title>
        <meta name="description" content={WRAPPED_2025_SEO.description} />
        <link rel="canonical" href={WRAPPED_2025_SEO.url} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={WRAPPED_2025_SEO.url} />
        <meta property="og:title" content={WRAPPED_2025_SEO.title} />
        <meta property="og:description" content={WRAPPED_2025_SEO.description} />
        <meta property="og:image" content={WRAPPED_2025_SEO.image} />
        
        {/* Twitter Card */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={WRAPPED_2025_SEO.url} />
        <meta property="twitter:title" content={WRAPPED_2025_SEO.title} />
        <meta property="twitter:description" content={WRAPPED_2025_SEO.description} />
        <meta property="twitter:image" content={WRAPPED_2025_SEO.image} />
        
        {/* Additional SEO meta tags */}
        <meta name="keywords" content="train delays, European trains, train punctuality, railway statistics, train reliability, Deutsche Bahn, SNCF, SBB, train delays 2025, European rail, Zugfinder, WarscheinlichAnkommen, BahnExpert, Trainline" />
        <meta name="author" content="chuuchuu" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        
        {/* Structured Data - JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "European Train Punctuality Rankings 2025 | chuuchuu Wrapped",
            "description": "Explore the 2025 rankings of European train punctuality by country. See which countries performed best, discover peak chaos hours, and find out which trains and stations had the most delays.",
            "url": "https://chuuchuu.com/2025wrapped",
            "inLanguage": "en",
            "isPartOf": {
              "@type": "WebSite",
              "name": "chuuchuu",
              "url": "https://chuuchuu.com"
            },
            "about": {
              "@type": "Thing",
              "name": "European Train Punctuality Statistics 2025"
            },
            "datePublished": "2025-01-01",
            "dateModified": "2025-01-01"
          })}
        </script>
        
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700&display=swap" rel="stylesheet" />
      </Helmet>
      <PageContainer>
        <ScrollProgress ref={scrollProgressRef} />

        {/* Hero Section */}
        <HeroSection>
          <StarField>
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`,
                  animationDuration: `${2 + Math.random() * 3}s`
                }}
              />
            ))}
          </StarField>
          <TrainCarousel style={{ top: '10%' }}>
            <TrainTrack speed={35}>
              {[...Array(20)].map((_, i) => (
                <TrainIcon key={i}>
                  <img src="/images/chuuhuu-icon-white.svg" alt="chuuchuu" />
                </TrainIcon>
              ))}
              {/* Duplicate for seamless loop */}
              {[...Array(20)].map((_, i) => (
                <TrainIcon key={`duplicate-${i}`}>
                  <img src="/images/chuuhuu-icon-white.svg" alt="chuuchuu" />
                </TrainIcon>
              ))}
              {/* Third set for extra smoothness */}
              {[...Array(20)].map((_, i) => (
                <TrainIcon key={`duplicate2-${i}`}>
                  <img src="/images/chuuhuu-icon-white.svg" alt="chuuchuu" />
                </TrainIcon>
              ))}
            </TrainTrack>
          </TrainCarousel>
          <TrainCarousel style={{ top: '30%' }}>
            <TrainTrack reverse speed={40}>
              {[...Array(20)].map((_, i) => (
                <TrainIcon key={i}>
                  <img src="/images/chuuhuu-icon-white.svg" alt="chuuchuu" />
                </TrainIcon>
              ))}
              {/* Duplicate for seamless loop */}
              {[...Array(20)].map((_, i) => (
                <TrainIcon key={`duplicate-${i}`}>
                  <img src="/images/chuuhuu-icon-white.svg" alt="chuuchuu" />
                </TrainIcon>
              ))}
              {/* Third set for extra smoothness */}
              {[...Array(20)].map((_, i) => (
                <TrainIcon key={`duplicate2-${i}`}>
                  <img src="/images/chuuhuu-icon-white.svg" alt="chuuchuu" />
                </TrainIcon>
              ))}
            </TrainTrack>
          </TrainCarousel>
          <TrainCarousel style={{ top: '50%' }}>
            <TrainTrack speed={28}>
              {[...Array(20)].map((_, i) => (
                <TrainIcon key={i}>
                  <img src="/images/chuuhuu-icon-white.svg" alt="chuuchuu" />
                </TrainIcon>
              ))}
              {/* Duplicate for seamless loop */}
              {[...Array(20)].map((_, i) => (
                <TrainIcon key={`duplicate-${i}`}>
                  <img src="/images/chuuhuu-icon-white.svg" alt="chuuchuu" />
                </TrainIcon>
              ))}
              {/* Third set for extra smoothness */}
              {[...Array(20)].map((_, i) => (
                <TrainIcon key={`duplicate2-${i}`}>
                  <img src="/images/chuuhuu-icon-white.svg" alt="chuuchuu" />
                </TrainIcon>
              ))}
            </TrainTrack>
          </TrainCarousel>
          <TrainCarousel style={{ top: '70%' }}>
            <TrainTrack reverse speed={32}>
              {[...Array(20)].map((_, i) => (
                <TrainIcon key={i}>
                  <img src="/images/chuuhuu-icon-white.svg" alt="chuuchuu" />
                </TrainIcon>
              ))}
              {/* Duplicate for seamless loop */}
              {[...Array(20)].map((_, i) => (
                <TrainIcon key={`duplicate-${i}`}>
                  <img src="/images/chuuhuu-icon-white.svg" alt="chuuchuu" />
                </TrainIcon>
              ))}
              {/* Third set for extra smoothness */}
              {[...Array(20)].map((_, i) => (
                <TrainIcon key={`duplicate2-${i}`}>
                  <img src="/images/chuuhuu-icon-white.svg" alt="chuuchuu" />
                </TrainIcon>
              ))}
            </TrainTrack>
          </TrainCarousel>
          <HeroContent>
            <HeroTitle>{t('wrapped2025.hero.title')}</HeroTitle>
            <HeroSubtitle>{t('wrapped2025.hero.subtitle')}</HeroSubtitle>
            <ScrollHint>{t('wrapped2025.hero.scrollHint')}</ScrollHint>
          </HeroContent>
        </HeroSection>

        {/* Big Number Section */}
        <Section id="bigNumber">
          <ContentCard visible={visibleSections.bigNumber}>
            <SectionTitle style={{ marginBottom: '3rem' }}>{t('wrapped2025.bigNumber.sectionTitle')}</SectionTitle>
            <BigNumberCard>
              <BigNumber>{t('wrapped2025.bigNumber.bigNumber')}</BigNumber>
              <p style={{ fontSize: '1.25rem', color: colors.slate300, marginBottom: '1rem' }}>
                {t('wrapped2025.bigNumber.description')}
              </p>
              <p style={{ fontSize: '1.125rem', color: colors.slate400 }}>
                {t('wrapped2025.bigNumber.subDescription')}
              </p>
            </BigNumberCard>
            <StatGrid>
              <StatCard>
                <StatValue color={colors.blue400}>17.3M</StatValue>
                <StatLabel>{t('wrapped2025.bigNumber.totalArrivals')}</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue color={colors.emerald400}>80.9%</StatValue>
                <StatLabel>{t('wrapped2025.bigNumber.overallPunctuality')}</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue color={colors.orange400}>19.1%</StatValue>
                <StatLabel>{t('wrapped2025.bigNumber.trainsDelayed')}</StatLabel>
              </StatCard>
            </StatGrid>
          </ContentCard>
        </Section>

        {/* Peak Chaos Section */}
        <Section id="peakChaos">
          <SectionWrapper maxWidth="80rem">
            <ContentCard visible={visibleSections.peakChaos}>
              <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
                <Icon name="alertTriangle" size={64} color={colors.orange400} />
                <SectionTitle style={{ marginTop: '1.5rem' }}>{t('wrapped2025.peakChaos.sectionTitle')}</SectionTitle>
                <SectionSubtitle>{t('wrapped2025.peakChaos.sectionSubtitle')}</SectionSubtitle>
              </div>
              <PeakChaosCard>
                <TimeDisplay>
                  <div>{t('wrapped2025.peakChaos.time')}</div>
                  <p>{t('wrapped2025.peakChaos.timeDescription')}</p>
                </TimeDisplay>
                <BarChart>
                  {hourlyDelayData.map((data, index) => {
                    const percentage = delayPercentages[index] || 0;
                    // Ensure percentage is a valid number and at least 0.5% for visibility
                    const barHeight = isNaN(percentage) || percentage < 0 ? 0 : Math.max(percentage, 0.5);
                    // Convert delaySum from seconds to hours and round to nearest integer
                    const delayHours = Math.round(data.delaySum / 3600);
                    // Format with comma separators
                    const formattedDelay = delayHours.toLocaleString('en-US');
                    return (
                      <BarWrapper key={data.hour}>
                        <Tooltip className="tooltip">
                          <TooltipTitle>{t('wrapped2025.peakChaos.tooltipTitle')}</TooltipTitle>
                          <TooltipValue>{formattedDelay} {t('wrapped2025.peakChaos.tooltipUnit')}</TooltipValue>
                        </Tooltip>
                        <Bar
                          height={barHeight}
                          visible={visibleSections.peakChaos}
                          delay={data.hour * 50}
                        />
                        <HourLabel>{data.hour}</HourLabel>
                      </BarWrapper>
                    );
                  })}
                </BarChart>
                <InfoBox>
                  <InfoHeader>
                    <Icon name="moon" size={32} color={colors.blue400} />
                    <InfoTitle style={{ marginBottom: 0 }}>{t('wrapped2025.peakChaos.nightOwlTitle')}</InfoTitle>
                  </InfoHeader>
                  <InfoText dangerouslySetInnerHTML={{ __html: t('wrapped2025.peakChaos.nightOwlText') }} />
                </InfoBox>
              </PeakChaosCard>
            </ContentCard>
          </SectionWrapper>
        </Section>

        {/* Hottest Day Section */}
        <Section id="hottestDay">
          <SectionWrapper>
            <ContentCard visible={visibleSections.hottestDay}>
              <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
                <Icon name="sun" size={64} color={colors.orange500} />
                <SectionTitle style={{ marginTop: '1.5rem' }}>{t('wrapped2025.hottestDay.sectionTitle')}</SectionTitle>
                <SectionSubtitle>{t('wrapped2025.hottestDay.sectionSubtitle')}</SectionSubtitle>
              </div>
              <DayCard variant="worst">
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                  <DayDate variant="worst">{t('wrapped2025.hottestDay.date')}</DayDate>
                  <DayTitle>{t('wrapped2025.hottestDay.title')}</DayTitle>
                </div>
                <DayStatsGrid>
                  <DayStatCard>
                    <StatValue color={colors.red400}>32.8°C</StatValue>
                    <StatLabel>{t('wrapped2025.hottestDay.maxTemp')}</StatLabel>
                  </DayStatCard>
                  <DayStatCard>
                    <StatValue color={colors.red400}>18 min</StatValue>
                    <StatLabel>{t('wrapped2025.hottestDay.avgDelay')}</StatLabel>
                  </DayStatCard>
                  <DayStatCard>
                    <StatValue color={colors.red400}>64.6%</StatValue>
                    <StatLabel>{t('wrapped2025.hottestDay.punctuality')}</StatLabel>
                  </DayStatCard>
                  <DayStatCard>
                    <StatValue color={colors.red400}>5.3M</StatValue>
                    <StatLabel>{t('wrapped2025.hottestDay.totalDelayMin')}</StatLabel>
                  </DayStatCard>
                </DayStatsGrid>
                <div style={{ background: 'rgba(15, 23, 42, 0.5)', borderRadius: '0.75rem', padding: '1.5rem' }}>
                  <p style={{ color: colors.slate300, lineHeight: '1.75' }}>
                    {t('wrapped2025.hottestDay.description')}
                  </p>
                </div>
              </DayCard>
            </ContentCard>
          </SectionWrapper>
        </Section>

        {/* Christmas Miracle */}
        <Section id="christmas">
          <SectionWrapper>
            <ContentCard visible={visibleSections.christmas}>
              <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
                <Icon name="snowflake" size={64} color={colors.blue400} animate />
                <SectionTitle style={{ marginTop: '1.5rem' }}>{t('wrapped2025.christmas.sectionTitle')}</SectionTitle>
                <SectionSubtitle>{t('wrapped2025.christmas.sectionSubtitle')}</SectionSubtitle>
              </div>
              <DayCard variant="best">
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                  <DayDate variant="best">{t('wrapped2025.christmas.date')}</DayDate>
                  <DayTitle style={{ marginBottom: '0.25rem' }}>{t('wrapped2025.christmas.title')}</DayTitle>
                  <div style={{ fontSize: '0.875rem', color: colors.slate400, marginTop: '0', fontStyle: 'italic' }}>
                    {t('wrapped2025.christmas.note')}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
                  <DayStatCard>
                    <StatValue color={colors.emerald400}>87.8%</StatValue>
                    <StatLabel>{t('wrapped2025.hottestDay.punctuality')}</StatLabel>
                  </DayStatCard>
                  <DayStatCard>
                    <StatValue color={colors.emerald400}>5.2 min</StatValue>
                    <StatLabel>{t('wrapped2025.hottestDay.avgDelay')}</StatLabel>
                  </DayStatCard>
                </div>
                <div style={{ background: 'rgba(15, 23, 42, 0.5)', borderRadius: '0.75rem', padding: '1.5rem', textAlign: 'center' }}>
                  <p style={{ color: colors.slate300, lineHeight: '1.75' }}>
                    {t('wrapped2025.christmas.description')}
                  </p>
                </div>
              </DayCard>
            </ContentCard>
          </SectionWrapper>
        </Section>

        {/* Country Rankings */}
        <Section id="countries">
          <SectionWrapper maxWidth="80rem">
            <ContentCard visible={visibleSections.countries}>
              <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
                <Icon name="award" size={64} color={colors.yellow400} />
                <SectionTitle style={{ marginTop: '1.5rem' }}>{t('wrapped2025.countries.sectionTitle')}</SectionTitle>
                <SectionSubtitle>{t('wrapped2025.countries.sectionSubtitle')}</SectionSubtitle>
              </div>
              <CountryList>
                {countries.map((country, index) => {
                  // Map country codes to flag emojis
                  const flagEmojis: Record<string, string> = {
                    'CH': '🇨🇭',
                    'NL': '🇳🇱',
                    'BE': '🇧🇪',
                    'AT': '🇦🇹',
                    'FR': '🇫🇷',
                    'IT': '🇮🇹',
                    'DE': '🇩🇪',
                  };
                  const flag = flagEmojis[country.code] || '🏳️';
                  
                  return (
                    <CountryCard
                      key={country.code}
                      visible={visibleSections.countries}
                      delay={index * 100}
                    >
                      <CountryHeader>
                        <RankNumber rank={country.rank}>#{country.rank}</RankNumber>
                        <CountryInfo>
                          <div>{flag} {country.name}</div>
                          <ProgressBar>
                            <ProgressFill
                              width={country.punctuality}
                              visible={visibleSections.countries}
                              color={country.color}
                            />
                          </ProgressBar>
                        </CountryInfo>
                        <CountryStats>
                          <CountryStat>
                            <div style={{ color: country.punctuality > 90 ? colors.emerald400 : country.punctuality > 80 ? colors.blue400 : country.punctuality > 70 ? colors.yellow400 : country.punctuality > 60 ? colors.orange400 : colors.red400 }}>
                              {country.punctuality.toFixed(1)}%
                            </div>
                            <div>{t('wrapped2025.countries.onTime')}</div>
                          </CountryStat>
                          <CountryStat>
                            <div style={{ color: colors.slate400 }}>{country.delay}y</div>
                            <div>{t('wrapped2025.countries.totalDelays')}</div>
                          </CountryStat>
                        </CountryStats>
                      </CountryHeader>
                    </CountryCard>
                  );
                })}
              </CountryList>
              <InfoBox style={{ marginTop: '3rem', background: 'linear-gradient(to bottom right, rgba(239, 68, 68, 0.2), rgba(249, 115, 22, 0.2))', border: `1px solid rgba(239, 68, 68, 0.3)` }}>
                <InfoHeader>
                  <Icon name="trendingDown" size={32} color={colors.red400} />
                  <InfoTitle style={{ marginBottom: 0, color: colors.red400 }}>{t('wrapped2025.countries.germanyProblemTitle')}</InfoTitle>
                </InfoHeader>
                <InfoText dangerouslySetInnerHTML={{ __html: t('wrapped2025.countries.germanyProblemText') }} />
              </InfoBox>
            </ContentCard>
          </SectionWrapper>
        </Section>

        {/* Night Train Nightmares */}
        <Section id="nightTrains">
          <SectionWrapper>
            <ContentCard visible={visibleSections.nightTrains}>
              <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
                <Icon name="moon" size={64} color={colors.cyan400} />
                <SectionTitle style={{ marginTop: '1.5rem' }}>{t('wrapped2025.nightTrains.sectionTitle')}</SectionTitle>
                <SectionSubtitle>{t('wrapped2025.nightTrains.sectionSubtitle')}</SectionSubtitle>
              </div>
              <PeakChaosCard>
                <TrainList>
                  {topDelayed.map((train, index) => (
                    <TrainCard key={train.route} visible={visibleSections.nightTrains} delay={index * 150}>
                      <TrainHeader>
                        <TrainRoute>
                          <TrainLineInfo>
                            <TrainLineName>{train.route}</TrainLineName>
                            <TrainRouteText>{train.from} → {train.to}</TrainRouteText>
                          </TrainLineInfo>
                        </TrainRoute>
                        <TrainDelay>
                          <div>+{train.delay.toFixed(1)}</div>
                          <div>{t('wrapped2025.nightTrains.minutesAvg')}</div>
                        </TrainDelay>
                      </TrainHeader>
                    </TrainCard>
                  ))}
                </TrainList>
                <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1.5rem', border: `1px solid ${colors.blue400}`, marginBottom: '1.5rem' }}>
                  <p style={{ color: colors.slate300, lineHeight: '1.75' }}>
                    <strong style={{ color: colors.blue400 }}>{t('wrapped2025.nightTrains.whyTitle')}</strong> {t('wrapped2025.nightTrains.whyText')}
                  </p>
                </div>
                <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1.5rem', border: `1px solid ${colors.emerald400}` }}>
                  <div style={{ fontWeight: 700, fontSize: '1.125rem', marginBottom: '1rem', color: colors.emerald400, textAlign: 'center' }}>{t('wrapped2025.nightTrains.mostDelayedDaytimeTitle')}</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                    <TrainLineInfo>
                      <TrainLineName>EC 8</TrainLineName>
                      <TrainRouteText>Zürich → Hamburg</TrainRouteText>
                    </TrainLineInfo>
                    <TrainDelay>
                      <div>+59.4</div>
                      <div>{t('wrapped2025.nightTrains.minutesAvg')}</div>
                    </TrainDelay>
                  </div>
                </div>
              </PeakChaosCard>
            </ContentCard>
          </SectionWrapper>
        </Section>

        {/* Station Lottery */}
        <Section id="stations">
          <SectionWrapper maxWidth="80rem">
            <ContentCard visible={visibleSections.stations}>
              <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
                <Icon name="mapPin" size={64} color={colors.blue400} />
                <SectionTitle style={{ marginTop: '1.5rem' }}>{t('wrapped2025.stations.sectionTitle')}</SectionTitle>
                <SectionSubtitle>{t('wrapped2025.stations.sectionSubtitle')}</SectionSubtitle>
              </div>
              <StationGrid>
                <StationCard variant="best" visible={visibleSections.stations}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <Icon name="trendingUp" size={40} color={colors.emerald400} />
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: colors.emerald400 }}>{t('wrapped2025.stations.bestStation')}</div>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ fontSize: '2rem', fontWeight: 900, marginBottom: '0.5rem', color: 'white' }}>
                      Genève-Aéroport
                    </div>
                    <div style={{ color: colors.slate300 }}>Switzerland</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                    <DayStatCard>
                      <StatValue color={colors.emerald400}>97.96%</StatValue>
                      <StatLabel>Punctuality</StatLabel>
                    </DayStatCard>
                    <DayStatCard>
                      <StatValue color={colors.emerald400}>0 min</StatValue>
                      <StatLabel>Avg delay</StatLabel>
                    </DayStatCard>
                  </div>
                  <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: `1px solid rgba(16, 185, 129, 0.3)` }}>
                    <p style={{ color: colors.slate300, fontSize: '0.875rem', lineHeight: '1.5' }}>
                      {t('wrapped2025.stations.bestStationDescription')}
                    </p>
                  </div>
                </StationCard>
                <StationCard variant="worst" visible={visibleSections.stations}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <Icon name="trendingDown" size={40} color={colors.red400} />
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: colors.red400 }}>{t('wrapped2025.stations.worstStation')}</div>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ fontSize: '2rem', fontWeight: 900, marginBottom: '0.5rem', color: 'white' }}>
                      Bonn Hbf
                    </div>
                    <div style={{ color: colors.slate300 }}>Germany</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                    <DayStatCard>
                      <StatValue color={colors.red400}>26.82%</StatValue>
                      <StatLabel>Punctuality</StatLabel>
                    </DayStatCard>
                    <DayStatCard>
                      <StatValue color={colors.red400}>18.9 min</StatValue>
                      <StatLabel>Avg delay</StatLabel>
                    </DayStatCard>
                  </div>
                  <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: `1px solid rgba(239, 68, 68, 0.3)` }}>
                    <p style={{ color: colors.slate300, fontSize: '0.875rem', lineHeight: '1.5' }}>
                      {t('wrapped2025.stations.worstStationDescription')}
                    </p>
                  </div>
                </StationCard>
              </StationGrid>
              
              <StationMap
                stations={mapStations}
                center={mapCenter}
                zoom={mapZoom}
                mapboxAccessToken={mapboxToken}
              />

              </ContentCard>
            </SectionWrapper>
          </Section>

        {/* Dive Deep Section */}
        <DiveDeepSection id="diveDeep">
          <SectionWrapper maxWidth="80rem">
            <ContentCard visible={visibleSections.diveDeep}>
              <SectionTitle>{t('wrapped2025.diveDeep.sectionTitle')}</SectionTitle>
              <SectionSubtitle>
                {t('wrapped2025.diveDeep.sectionSubtitle')}
              </SectionSubtitle>
              
              <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'center' }}>
                <CountryDropdown
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value as CountryCode)}
                >
                  {(Object.keys(countryNames) as CountryCode[]).map(code => (
                    <option key={code} value={code}>
                      {countryFlags[code]}  {countryNames[code]}
                    </option>
                  ))}
                </CountryDropdown>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
                {lineData.length > 0 ? (
                  <LinesTable data={lineData} />
                ) : (
                  <div style={{ padding: '2rem', textAlign: 'center', color: colors.slate300 }}>
                    {t('wrapped2025.diveDeep.loadingTrainData')}
                  </div>
                )}
                {stationData.length > 0 ? (
                  <StationsTable data={stationData} />
                ) : (
                  <div style={{ padding: '2rem', textAlign: 'center', color: colors.slate300 }}>
                    {t('wrapped2025.diveDeep.loadingStationData')}
                  </div>
                )}
              </div>
            </ContentCard>
          </SectionWrapper>
        </DiveDeepSection>

        {/* Closing Section */}
        <ClosingSection>
          <SectionWrapper maxWidth="48rem">
            <div style={{ textAlign: 'center', zIndex: 10, position: 'relative' }}>
                <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <img 
                    src="/images/chuuchuuwrapped.png" 
                    alt="chuuchuu wrapped" 
                    style={{ width: '200px', height: 'auto' }}                  />
                </div>
                <h2 style={{ fontSize: '2.25rem', fontWeight: 700, marginBottom: '1.5rem' }}>
                  {t('wrapped2025.closing.title')}
                </h2>
                <p style={{ fontSize: '1.25rem', color: colors.slate300, marginBottom: '1.5rem', lineHeight: '1.75' }}>
                  {t('wrapped2025.closing.description')}
                </p>
                <CTACard>
                  <CTATitle style={{ marginTop: 0 }}>{t('wrapped2025.closing.ctaTitle')}</CTATitle>
                  <CTASubtitle>{t('wrapped2025.closing.ctaSubtitle')}</CTASubtitle>
                  <CTAButton onClick={() => navigate('/')}>
                    {t('wrapped2025.closing.ctaButton')}
                  </CTAButton>
                </CTACard>
                <SecondaryCTACard>
                  <SecondaryCTATitle>{t('wrapped2025.closing.secondaryCtaTitle')}</SecondaryCTATitle>
                  <SecondaryCTASubtitle>{t('wrapped2025.closing.secondaryCtaSubtitle')}</SecondaryCTASubtitle>
                  <SecondaryCTAButton onClick={() => navigate('/data')}>
                    {t('wrapped2025.closing.secondaryCtaButton')}
                  </SecondaryCTAButton>
                </SecondaryCTACard>
                <div style={{ color: colors.slate400, fontSize: '0.875rem' }}>
                  <p>{t('wrapped2025.closing.dataNote')}</p>
                  <p style={{ marginTop: '0.5rem' }}>{t('wrapped2025.closing.trackedBy')}</p>
                </div>
              </div>
            </SectionWrapper>
        </ClosingSection>
      </PageContainer>
    </>
  );
};

export default Wrapped2025Page;
